import { faEnvelope, faFilePdf, faGrip } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from '@/components/atoms';
import { AvatarWithName, SummarySectionWithTitle } from '@/components/molecules';

interface PlayerReceiptProps {
	userProfilePicture: string;
	firstName: string;
	lastName: string;
	registrationType: string;
	teamName: string;
	qty: number;
	price: number;
	players: { firstName: string; lastName: string }[];
}

export const PlayerReceipt = ({ userProfilePicture, firstName, lastName, registrationType, teamName, qty, price, players }: PlayerReceiptProps) => {
	return (
		<div className="flex w-full flex-col rounded-xl bg-white p-4 shadow-md">
			<div className="flex items-center">
				<AvatarWithName src={userProfilePicture} alt={`${firstName} - profile picture`} firstName={firstName} lastName={lastName} />
			</div>

			<div className="flex flex-col gap-4">
				<div className="flex flex-col gap-4 pt-6">
					<div className="flex flex-col-reverse justify-between gap-4 md:flex-row">
						<Button
							type="button"
							className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 "
						>
							<FontAwesomeIcon icon={faGrip} className="text-purple-600" />
							Go to team dashboard
						</Button>
						<div className="flex w-full justify-between gap-4 md:w-fit	">
							<Button
								type="button"
								className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 "
							>
								<FontAwesomeIcon icon={faEnvelope} className="size-5" />
								Email receipt
							</Button>
							<Button
								type="button"
								className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 "
							>
								<FontAwesomeIcon icon={faFilePdf} className="size-5" />
								Print receipt
							</Button>
						</div>
					</div>
					<div className="text-2xl font-semibold text-gray-900">Receipt</div>
					<div className="flex flex-col justify-between gap-4 sm:flex-row sm:gap-0">
						<div>
							<p className="text-sm  text-gray-900">
								<span className="font-bold">Date:</span> Friday, May 10, 2024
							</p>
							<p className="text-sm  text-gray-900">
								<span className="font-bold">Order ID:</span> 22320638
							</p>
						</div>

						<div className="max-w-[180px]">
							<p className="mb-2 text-sm font-bold text-gray-900">Billed to:</p>
							<p className="text-sm text-gray-900">Visa..0000 Giorgio Morder 945 Madison Avenue New York, NY 10021</p>
						</div>
					</div>
				</div>
				<SummarySectionWithTitle title="Summary">
					<div>
						<div className="flex flex-col text-sm font-semibold leading-5 text-gray-900 sm:h-10 sm:flex-row sm:items-center sm:justify-between">
							<span className="py-2.5">{registrationType}</span>

							<div>
								<span className="inline-block min-w-16 pl-0 text-left sm:p-2.5">Qty</span>
								<span className="inline-block min-w-20 p-2.5 text-left">Price</span>
								<span className="inline-block min-w-20 p-2.5 text-left">Total</span>
							</div>
						</div>

						<div className="flex flex-col text-gray-900 sm:h-10 sm:flex-row sm:items-center sm:justify-between">
							<span className="hidden h-10 py-2.5 text-base font-medium leading-6 sm:inline-block">{teamName}</span>

							<div>
								<span className="inline-block min-w-16 pl-0 text-left text-sm font-normal leading-5 sm:p-2.5">{qty}</span>
								<span className="inline-block min-w-20 p-2.5 text-left text-sm font-normal leading-5">${price}</span>
								<span className="inline-block min-w-20 p-2.5 text-left text-sm font-semibold leading-5">${qty * price}</span>
							</div>
						</div>

						<span className="mt-6 inline-block h-10 py-2.5 text-base font-medium leading-6 sm:hidden">{teamName}</span>
						{players.map((item, index) => {
							return (
								<div key={index}>
									<p className="text-xs font-medium leading-[18px] text-gray-600">
										<span>
											{item.firstName} {item.lastName}
										</span>
									</p>
								</div>
							);
						})}
					</div>
				</SummarySectionWithTitle>
			</div>

			<div className="mt-6 flex flex-col items-center justify-center">
				<span className="mb-3 text-xl font-semibold leading-[30px] text-gray-900">Remaining Balance</span>

				<div className="mb-8 flex items-center justify-center gap-1">
					<span className="text-sm font-normal leading-5 text-gray-400">USD</span>
					<span className="text-lg font-semibold not-italic leading-7 text-gray-900">${qty * price}</span>
				</div>
			</div>
		</div>
	);
};

export const TestPlayerReceipt = () => {
	const mockedData = {
		userProfilePicture:
			'https://plus.unsplash.com/premium_photo-1683865776032-07bf70b0add1?q=80&w=2832&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
		firstName: 'John',
		lastName: 'Doe',
		registrationType: 'Team Registration',
		teamName: 'The Champions',
		qty: 3,
		price: 50,
		players: [
			{ firstName: 'Alice', lastName: 'Smith' },
			{ firstName: 'Bob', lastName: 'Johnson' },
			{ firstName: 'Charlie', lastName: 'Brown' }
		]
	};

	return <PlayerReceipt {...mockedData} />;
};
